import type { CompositeQueryDefinition } from '@/types/chartDataSource';
import type { TemplateQuery } from '@/services/api/dataApi';
import type {
  CreateResultMetadata,
  CreateTemplateResult,
} from './DataDiscoveryModal.createLogic';

export interface TemplateSaveMetadata {
  name: string;
  description: string;
  tags: string;
  folder: string;
}

export const EMPTY_TEMPLATE_SAVE_METADATA: TemplateSaveMetadata = {
  name: '',
  description: '',
  tags: '',
  folder: '',
};

const UNRESOLVED_PLACEHOLDER_PATTERN = /\{\{[^{}]*\}\}/;

function hasUnresolvedPlaceholder(value: string | null | undefined): boolean {
  return value ? UNRESOLVED_PLACEHOLDER_PATTERN.test(value) : false;
}

function normalizeLabel(value: string | null | undefined): string | null {
  if (hasUnresolvedPlaceholder(value)) {
    return null;
  }

  const normalized = value
    ?.replace(/^data\./, '')
    .replace(/\./g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (hasUnresolvedPlaceholder(normalized)) {
    return null;
  }

  return normalized && normalized.length > 0 ? normalized : null;
}

function stripImplementationFieldPrefix(value: string): string {
  return value
    .replace(/^(?:data|metadata)[._-]+/i, '')
    .replace(/^(?:data|metadata)(?=[A-Z])/, '');
}

function normalizeFieldPathLabel(value: string | null | undefined): string | null {
  if (hasUnresolvedPlaceholder(value)) {
    return null;
  }

  const normalized = normalizeLabel(
    value ? stripImplementationFieldPrefix(value) : value,
  );
  return normalized && normalized.length > 0 ? normalized : null;
}

function formatReadableLabel(value: string | null | undefined): string | null {
  const normalized = normalizeLabel(value)?.replace(
    /([a-z0-9])([A-Z])/g,
    '$1 $2',
  );
  return normalized && normalized.length > 0 ? normalized : null;
}

function formatReadableFieldPathLabel(
  value: string | null | undefined,
): string | null {
  const normalized = normalizeFieldPathLabel(value)?.replace(
    /([a-z0-9])([A-Z])/g,
    '$1 $2',
  );
  return normalized && normalized.length > 0 ? normalized : null;
}

function formatReadableFieldLabel(
  value: string | null | undefined,
): string | null {
  const readableLabel = formatReadableFieldPathLabel(value);
  return readableLabel ? readableLabel.toLowerCase() : null;
}

function toTitleCase(value: string): string {
  const minorWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'by',
    'for',
    'from',
    'in',
    'of',
    'on',
    'or',
    'over',
    'the',
    'to',
    'vs',
    'with',
  ]);

  return value
    .split(' ')
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index > 0 && minorWords.has(lower)) {
        return lower;
      }

      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function trimLength(value: string, maxLength: number): string {
  return value.length <= maxLength ? value : value.slice(0, maxLength).trim();
}

const PROMPT_COMMAND_PREFIXES = [
  /^please\s+/i,
  /^(show|display|graph|plot|chart|list|get)\s+(me\s+)?/i,
  /^give\s+me\s+/i,
];

const GENERIC_PROMPT_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'average',
  'avg',
  'by',
  'count',
  'daily',
  'day',
  'days',
  'for',
  'from',
  'get',
  'graph',
  'hour',
  'hourly',
  'in',
  'last',
  'list',
  'max',
  'maximum',
  'mean',
  'me',
  'min',
  'minimum',
  'month',
  'monthly',
  'of',
  'on',
  'or',
  'over',
  'past',
  'per',
  'please',
  'plot',
  'show',
  'sum',
  'the',
  'to',
  'today',
  'total',
  'with',
  'year',
  'yearly',
  'yesterday',
]);

function removeLeadingPromptCommand(value: string): string {
  let cleaned = value.trim();

  for (const prefix of PROMPT_COMMAND_PREFIXES) {
    const next = cleaned.replace(prefix, '').trim();
    if (next !== cleaned && promptHasUsefulNameTokens(next)) {
      cleaned = next;
      break;
    }
  }

  return cleaned;
}

function promptHasUsefulNameTokens(value: string): boolean {
  const tokens = value.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  return tokens.some(
    (token) => !GENERIC_PROMPT_WORDS.has(token) && !/^\d+$/.test(token),
  );
}

function promptToTemplateName(prompt: string | undefined): string | null {
  if (hasUnresolvedPlaceholder(prompt)) {
    return null;
  }

  const cleanedPrompt = prompt
    ?.replace(/\?/g, '')
    .replace(/\s*\/\s*/g, ' by ')
    .trim();
  const commandlessPrompt = cleanedPrompt
    ? removeLeadingPromptCommand(cleanedPrompt)
    : null;
  const normalized = normalizeLabel(commandlessPrompt);
  if (!normalized || !promptHasUsefulNameTokens(normalized)) {
    return null;
  }

  return normalized ? trimLength(toTitleCase(normalized), 80) : null;
}

function uniqueLabels(labels: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const label of labels) {
    const normalized = normalizeLabel(label);
    if (!normalized) {
      continue;
    }

    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    unique.push(normalized);
  }

  return unique;
}

function uniqueReadableLabels(labels: Array<string | null | undefined>): string[] {
  return uniqueLabels(
    labels.flatMap((label) => {
      const readable = formatReadableLabel(label);
      return readable && !looksLikeImplementationIdentifier(label)
        ? [readable]
        : [];
    }),
  );
}

function looksLikeImplementationIdentifier(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }

  if (/^[a-f0-9]{24}$/i.test(trimmed)) {
    return true;
  }

  const normalized = normalizeLabel(trimmed)?.toLowerCase();
  if (!normalized) {
    return false;
  }

  return (
    /^(schema|group|source|template|query|collection)\s+[a-z0-9]/i.test(normalized) ||
    looksLikeTechnicalTimeFieldLabel(normalized)
  );
}

function looksLikeTechnicalTimeFieldLabel(value: string | null | undefined): boolean {
  const normalized = normalizeLabel(value)?.toLowerCase();
  if (!normalized) {
    return false;
  }

  return /^metadata\s+(timestamp|datetime|date|time)$/.test(normalized);
}

function looksLikeConcreteDateFragment(value: string | null | undefined): boolean {
  const trimmed = value?.trim();
  if (!trimmed) {
    return false;
  }

  if (/\d{4}-\d{2}-\d{2}/.test(trimmed) || /\d{2}:\d{2}/.test(trimmed)) {
    return true;
  }

  const normalized = normalizeLabel(trimmed);
  if (!normalized) {
    return false;
  }

  if (/^\d{1,4}$/.test(normalized)) {
    return true;
  }

  return /\b\d{4}\b.*\b\d{1,2}\b.*\b\d{1,2}\b/.test(normalized);
}

function isConciseMetadataTag(value: string | null | undefined): boolean {
  const normalized = normalizeLabel(value);
  if (!normalized) {
    return false;
  }

  return (
    !isGenericCountFieldLabel(normalized) &&
    !looksLikeImplementationIdentifier(value) &&
    !looksLikeConcreteDateFragment(value) &&
    normalized.length <= 48
  );
}

function formatReadableList(labels: string[]): string {
  if (labels.length <= 2) {
    return labels.join(' and ');
  }

  const leadingLabels = labels.slice(0, -1).join(', ');
  const finalLabel = labels[labels.length - 1];
  return `${leadingLabels}, and ${finalLabel}`;
}

function getCompositeSourceLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueLabels(
    definition.sources.map(
      (source) => source.label ?? source.query.schemaName ?? source.id,
    ),
  );
}

function getCompositeFieldLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueReadableLabels(definition.fields.map((field) => field.alias));
}

function getCompositeComputedFieldLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueLabels(
    (definition.computedFields ?? []).flatMap((field) => {
      const [labelCandidate] = field.name.split('=');
      const label = formatReadableFieldPathLabel(labelCandidate);
      return label && !looksLikeImplementationIdentifier(labelCandidate)
        ? [label]
        : [];
    }),
  );
}

function buildTagString(labels: string[], preserveCase = false): string {
  return uniqueLabels(labels)
    .map((label) => (preserveCase ? label.trim() : label.toLowerCase()))
    .join(', ');
}

function buildCuratedTagString(labels: string[], maxTags: number): string {
  const uniqueTags = uniqueLabels(labels).filter(isConciseMetadataTag);
  const resultTypeTag = uniqueTags.find(
    (tag) => tag === 'Single Source' || tag === 'Combined Sources' || tag === 'Period Comparison',
  );
  const leadingTags = resultTypeTag
    ? uniqueTags.filter((tag) => tag !== resultTypeTag)
    : uniqueTags;
  const cappedTags = resultTypeTag
    ? [...leadingTags.slice(0, maxTags - 1), resultTypeTag]
    : leadingTags.slice(0, maxTags);

  return buildTagString(cappedTags, true);
}

function getAggregationLabel(aggregation: string): string {
  if (hasUnresolvedPlaceholder(aggregation)) {
    return 'Generated';
  }

  const normalized = aggregation.trim().toLowerCase();
  const labels: Record<string, string> = {
    avg: 'Average',
    count: 'Count',
    first: 'First',
    last: 'Last',
    max: 'Maximum',
    min: 'Minimum',
    sum: 'Total',
  };

  return (
    labels[normalized] ??
    toTitleCase(normalizeLabel(aggregation) ?? aggregation)
  );
}

function getMeasureFieldLabel(
  measure: TemplateQuery['measures'][number],
): string | null {
  return formatReadableFieldPathLabel(measure.field);
}

function getMeasureAliasLabel(
  measure: TemplateQuery['measures'][number],
): string | null {
  return formatReadableLabel(measure.alias);
}

function getUserFacingMeasureAliasLabel(
  measure: TemplateQuery['measures'][number],
): string | null {
  const aliasLabel = getMeasureAliasLabel(measure);
  return aliasLabel && !looksLikeImplementationIdentifier(measure.alias)
    ? aliasLabel
    : null;
}

function isGenericMeasureFieldLabel(value: string | null | undefined): boolean {
  const normalized = normalizeFieldPathLabel(value)?.toLowerCase();
  return normalized === 'value';
}

function isGenericCountFieldLabel(value: string | null | undefined): boolean {
  return value?.trim() === '*';
}

function isGenericCountMeasure(
  measure: TemplateQuery['measures'][number],
): boolean {
  return (
    measure.aggregation.trim().toLowerCase() === 'count' &&
    isGenericCountFieldLabel(measure.field)
  );
}

function getAliasMetricLabel(
  measure: TemplateQuery['measures'][number],
): string | null {
  const aliasLabel = getMeasureAliasLabel(measure);
  const fieldLabel = getMeasureFieldLabel(measure);
  return aliasLabel && isGenericMeasureFieldLabel(fieldLabel) ? aliasLabel : null;
}

function getMeasureSummary(measure: TemplateQuery['measures'][number]): string {
  const fieldLabel = getMeasureFieldLabel(measure) ?? 'value';
  const aliasLabel = getUserFacingMeasureAliasLabel(measure);
  const aliasMetricLabel = getAliasMetricLabel(measure);
  const aggregationLabel = getAggregationLabel(measure.aggregation);
  if (isGenericCountMeasure(measure)) {
    const aliasTitleLabel = aliasLabel ? toTitleCase(aliasLabel) : null;
    return aliasTitleLabel && aliasTitleLabel.toLowerCase() !== 'count'
      ? `${aggregationLabel} as ${aliasTitleLabel}`
      : aggregationLabel;
  }

  const metricLabel = `${aggregationLabel} ${toTitleCase(fieldLabel)}`;
  if (aliasMetricLabel) {
    return toTitleCase(aliasMetricLabel);
  }

  if (!aliasLabel) {
    return metricLabel;
  }

  const aliasTitleLabel = toTitleCase(aliasLabel);
  const aliasComparisonKey = aliasTitleLabel.toLowerCase();
  return aliasComparisonKey !== toTitleCase(fieldLabel).toLowerCase() &&
    aliasComparisonKey !== metricLabel.toLowerCase()
    ? `${metricLabel} as ${aliasTitleLabel}`
    : metricLabel;
}

function getPrimaryMeasureName(
  sourceName: string,
  measure: TemplateQuery['measures'][number] | undefined,
): string {
  if (!measure) {
    return sourceName;
  }

  const fieldLabel = getMeasureFieldLabel(measure);
  const aliasMetricLabel = getAliasMetricLabel(measure);
  const aggregationLabel = getAggregationLabel(measure.aggregation);
  if (isGenericCountMeasure(measure)) {
    const aliasLabel = getUserFacingMeasureAliasLabel(measure);
    const countLabel =
      aliasLabel && aliasLabel.toLowerCase() !== 'count'
        ? aliasLabel
        : aggregationLabel;
    return countLabel.toLowerCase().includes(sourceName.toLowerCase())
      ? countLabel
      : `${sourceName} ${countLabel}`;
  }

  if (aliasMetricLabel) {
    return aliasMetricLabel.toLowerCase().includes(sourceName.toLowerCase())
      ? aliasMetricLabel
      : `${sourceName} ${aliasMetricLabel}`;
  }

  if (!fieldLabel) {
    return sourceName;
  }

  return sourceName.toLowerCase().includes(fieldLabel.toLowerCase())
    ? `${aggregationLabel} ${sourceName}`
    : `${sourceName} ${aggregationLabel} ${fieldLabel}`;
}

function getTimeRangeLabel(
  timeRange: TemplateQuery['timeRange'],
  defaultTimeRange?: unknown,
): string | null {
  if (!timeRange) {
    return null;
  }

  if (timeRange.preset && !timeRange.preset.includes('{{')) {
    return timeRange.preset;
  }

  if (timeRange.from && timeRange.to) {
    return 'custom range';
  }

  if (timeRange.from) {
    return 'custom range';
  }

  if (timeRange.to) {
    return 'custom range';
  }

  if (typeof defaultTimeRange === 'string') {
    const normalizedDefault = defaultTimeRange.trim();
    if (normalizedDefault && !normalizedDefault.includes('{{')) {
      return normalizedDefault;
    }
  }

  return looksLikeTechnicalTimeFieldLabel(timeRange.field)
    ? null
    : normalizeLabel(timeRange.field);
}

function getVariableDefaultLabel(defaultValue: unknown): string | null {
  return typeof defaultValue === 'string' ? normalizeLabel(defaultValue) : null;
}

function getGroupGranularityLabel(
  group: Extract<NonNullable<TemplateQuery['groupBy']>[number], { field: string }>,
  defaultGranularity?: unknown,
): string | null {
  return (
    normalizeLabel(group.granularity) ??
    getVariableDefaultLabel(defaultGranularity)
  );
}

function getGroupByLabels(
  groupBy: TemplateQuery['groupBy'],
  defaultGranularity?: unknown,
): string[] {
  return uniqueLabels(
    (groupBy ?? []).flatMap((group) => {
      if (typeof group === 'string') {
        const groupLabel = formatReadableFieldLabel(group);
        return groupLabel && !looksLikeTechnicalTimeFieldLabel(group)
          ? [groupLabel]
          : [];
      }

      const granularityLabel = getGroupGranularityLabel(
        group,
        defaultGranularity,
      );
      const fieldLabel = formatReadableFieldLabel(group.field);
      if (granularityLabel) {
        return looksLikeTechnicalTimeFieldLabel(group.field)
          ? [granularityLabel]
          : [fieldLabel, granularityLabel];
      }

      return fieldLabel && !looksLikeTechnicalTimeFieldLabel(group.field)
        ? [fieldLabel]
        : [];
    }),
  );
}

function getGroupByDescription(
  groupBy: TemplateQuery['groupBy'],
  defaultGranularity?: unknown,
): string | null {
  const labels = (groupBy ?? []).flatMap((group) => {
    if (typeof group === 'string') {
      if (looksLikeTechnicalTimeFieldLabel(group)) {
        return [];
      }

      return formatReadableFieldLabel(group) ?? [];
    }

    const fieldLabel = formatReadableFieldLabel(group.field);
    const granularityLabel = getGroupGranularityLabel(
      group,
      defaultGranularity,
    );
    if (fieldLabel && granularityLabel) {
      return looksLikeTechnicalTimeFieldLabel(group.field)
        ? [granularityLabel]
        : [`${fieldLabel} (${granularityLabel})`];
    }

    return fieldLabel && !looksLikeTechnicalTimeFieldLabel(group.field)
      ? [fieldLabel]
      : [];
  });

  return labels.length > 0 ? labels.join(', ') : null;
}

function getGroupByNameSuffix(
  groupBy: TemplateQuery['groupBy'],
  defaultGranularity?: unknown,
): string | null {
  const firstGroup = groupBy?.[0];
  if (!firstGroup) {
    return null;
  }

  if (typeof firstGroup === 'string') {
    return looksLikeTechnicalTimeFieldLabel(firstGroup)
      ? null
      : formatReadableFieldLabel(firstGroup);
  }

  return (
    getGroupGranularityLabel(firstGroup, defaultGranularity) ??
    (looksLikeTechnicalTimeFieldLabel(firstGroup.field)
      ? null
      : formatReadableFieldLabel(firstGroup.field))
  );
}

function buildSingleSourceFallbackName(
  sourceName: string,
  query: TemplateQuery | undefined,
  defaultGranularity?: unknown,
): string {
  const baseName = getPrimaryMeasureName(sourceName, query?.measures[0]);
  const groupBySuffix = getGroupByNameSuffix(query?.groupBy, defaultGranularity);
  return trimLength(
    toTitleCase(groupBySuffix ? `${baseName} by ${groupBySuffix}` : baseName),
    80,
  );
}

const USER_FACING_FIELD_AGGREGATIONS = new Set(['first', 'last']);

function getUserFacingMeasureFieldLabels(
  query: TemplateQuery | undefined,
): string[] {
  return uniqueLabels(
    (query?.measures ?? []).flatMap((measure) => {
      const aliasLabel = getUserFacingMeasureAliasLabel(measure);
      return aliasLabel ? [toTitleCase(aliasLabel)] : [];
    }),
  );
}

function measureLooksCategoricalOrStatus(
  measure: TemplateQuery['measures'][number],
): boolean {
  const aggregation = measure.aggregation.trim().toLowerCase();
  const labelText = [
    getMeasureAliasLabel(measure),
    getMeasureFieldLabel(measure),
  ]
    .filter((label): label is string => Boolean(label))
    .join(' ')
    .toLowerCase();

  return (
    USER_FACING_FIELD_AGGREGATIONS.has(aggregation) ||
    /\b(status|state|category|categorical|type|mode)\b/.test(labelText)
  );
}

function shouldUseUserFacingFieldMetadata(
  query: TemplateQuery | undefined,
): boolean {
  const measures = query?.measures ?? [];
  if (measures.length < 2) {
    return false;
  }

  const fieldLabels = getUserFacingMeasureFieldLabels(query);
  return (
    fieldLabels.length === measures.length &&
    measures.some(measureLooksCategoricalOrStatus)
  );
}

function getCompositeSourceLabel(
  source: CompositeQueryDefinition['sources'][number],
): string {
  return (
    normalizeLabel(source.label) ??
    normalizeLabel(source.query.schemaName) ??
    normalizeLabel(source.query.schemaId) ??
    normalizeLabel(source.id) ??
    'Source'
  );
}

function formatTimeRangeLabel(
  timeRange: CompositeQueryDefinition['sources'][number]['query']['timeRange'],
): string | null {
  const label = getTimeRangeLabel(timeRange);
  return label ? formatReadableLabel(label) : null;
}

function getCompositeTimeRangeLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueLabels(
    definition.sources.flatMap((source) => {
      const label = formatTimeRangeLabel(source.query.timeRange);
      return label ? [label] : [];
    }),
  );
}

function getCompositeGranularityLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueReadableLabels(
    definition.sources.flatMap((source) =>
      (source.query.groupBy ?? []).flatMap((group) => {
        if (typeof group === 'string') {
          return [];
        }

        return group.granularity;
      }),
    ),
  );
}

function getCompositeTimeRangeDescription(
  definition: CompositeQueryDefinition,
): string {
  const labels = uniqueLabels(
    definition.sources.flatMap((source) => {
      const timeRangeLabel = formatTimeRangeLabel(source.query.timeRange);
      if (!timeRangeLabel) {
        return [];
      }

      return [`${getCompositeSourceLabel(source)}: ${timeRangeLabel}`];
    }),
  );

  return labels.length > 0 ? labels.join('; ') : 'all available time';
}

function getCompositeConciseTimeRangeDescription(
  definition: CompositeQueryDefinition,
): string {
  const timeRangeLabels = definition.sources.flatMap((source) => {
    const timeRangeLabel = formatTimeRangeLabel(source.query.timeRange);
    return timeRangeLabel ? [timeRangeLabel] : [];
  });
  const uniqueTimeRangeLabels = uniqueLabels(timeRangeLabels);

  if (
    uniqueTimeRangeLabels.length === 1 &&
    timeRangeLabels.length === definition.sources.length
  ) {
    return uniqueTimeRangeLabels[0];
  }

  return getCompositeTimeRangeDescription(definition);
}

function formatCompositeJoinKeyLabel(
  joinKey: CompositeQueryDefinition['joinKeys'][number],
): string {
  const kind = normalizeLabel(joinKey.kind) ?? 'exact';
  const granularity = normalizeLabel(joinKey.granularity);
  const strategy = granularity ? `${kind} ${granularity}` : kind;

  return `${joinKey.left} = ${joinKey.right} (${strategy})`;
}

function getCompositeJoinDescription(
  definition: CompositeQueryDefinition,
): string {
  const joinKeyLabels = definition.joinKeys.map(formatCompositeJoinKeyLabel);
  const keySummary =
    joinKeyLabels.length > 0 ? ` on ${joinKeyLabels.join(', ')}` : '';

  return `${definition.joinType} join${keySummary}`;
}

function getCompositeJoinSummary(
  definition: CompositeQueryDefinition,
): string {
  const joinKey = definition.joinKeys[0];
  if (definition.joinKeys.length !== 1 || !joinKey) {
    return getCompositeJoinDescription(definition);
  }

  const kind = normalizeLabel(joinKey.kind) ?? 'exact';
  const granularity = normalizeLabel(joinKey.granularity);
  const strategy = granularity ? `${kind} ${granularity}` : kind;
  const keySummary =
    joinKey.left === joinKey.right
      ? joinKey.left
      : `${joinKey.left} = ${joinKey.right}`;

  return `${definition.joinType} ${strategy} match on ${keySummary}`;
}

function getPeriodComparisonJoinSummary(
  definition: CompositeQueryDefinition,
): string {
  const joinKey = definition.joinKeys[0];
  if (definition.joinKeys.length !== 1 || !joinKey) {
    return getCompositeJoinDescription(definition);
  }

  const kind = joinKey.kind ?? 'exact';
  const keySummary =
    joinKey.left === joinKey.right
      ? joinKey.left
      : `${joinKey.left} = ${joinKey.right}`;

  return `${definition.joinType} ${kind} match on ${keySummary}`;
}

function getCompositeFieldDescription(
  definition: CompositeQueryDefinition,
): string {
  const fieldLabels = getCompositeFieldLabels(definition);

  return fieldLabels.length > 0 ? fieldLabels.join(', ') : 'generated fields';
}

function isJoinOrTimeFieldLabel(
  label: string,
  definition: CompositeQueryDefinition,
): boolean {
  const normalizedLabel = normalizeLabel(label)?.toLowerCase();
  if (!normalizedLabel) {
    return true;
  }

  if (/^(date|time|datetime|timestamp)$/.test(normalizedLabel)) {
    return true;
  }

  return definition.joinKeys.some((joinKey) => {
    const left = normalizeLabel(joinKey.left)?.toLowerCase();
    const right = normalizeLabel(joinKey.right)?.toLowerCase();
    return normalizedLabel === left || normalizedLabel === right;
  });
}

function getCompositeMetricFieldLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueReadableLabels(
    definition.fields.flatMap((field) => {
      const fieldLabel = formatReadableFieldPathLabel(field.field);
      const label =
        fieldLabel && !isGenericMeasureFieldLabel(fieldLabel)
          ? fieldLabel
          : formatReadableLabel(field.alias);
      return label && !isJoinOrTimeFieldLabel(label, definition) ? [label] : [];
    }),
  );
}

function getCompositeComputedFieldDescription(
  definition: CompositeQueryDefinition,
): string | null {
  const computedFieldLabels = getCompositeComputedFieldLabels(definition).map(
    toTitleCase,
  );

  return computedFieldLabels.length > 0
    ? computedFieldLabels.join(', ')
    : null;
}

function getCompositeComputedFieldSentence(
  definition: CompositeQueryDefinition,
): string | null {
  const computedFieldSummary = getCompositeComputedFieldDescription(definition);
  if (!computedFieldSummary) {
    return null;
  }

  const computedFieldCount = getCompositeComputedFieldLabels(definition).length;
  const label = computedFieldCount === 1 ? 'Computed field' : 'Computed fields';
  return `${label}: ${computedFieldSummary}.`;
}

function textSuggestsPeriodComparison(text: string): boolean {
  return /\b(compare|comparison|versus|vs\.?|against|difference|delta|change|today|yesterday|previous|current)\b/i.test(
    text,
  );
}

function allLabelsMatch(labels: string[]): boolean {
  const [firstLabel, ...remainingLabels] = labels;
  return (
    firstLabel !== undefined &&
    remainingLabels.every((label) => label === firstLabel)
  );
}

function sourceIdentityMatches(
  sources: CompositeQueryDefinition['sources'],
): boolean {
  const schemaIds = sources.flatMap((source) => {
    const label = normalizeLabel(source.query.schemaId);
    return label ? [label.toLowerCase()] : [];
  });
  const schemaNames = sources.flatMap((source) => {
    const label = normalizeLabel(source.query.schemaName);
    return label ? [label.toLowerCase()] : [];
  });

  return (
    (schemaIds.length === sources.length && allLabelsMatch(schemaIds)) ||
    (schemaNames.length === sources.length && allLabelsMatch(schemaNames))
  );
}

function getSourceMeasureSignature(
  source: CompositeQueryDefinition['sources'][number],
): string | null {
  const measureLabels = source.query.measures.flatMap((measure) => {
    const field = normalizeLabel(measure.field);
    const aggregation = normalizeLabel(measure.aggregation);
    return field && aggregation
      ? [`${field.toLowerCase()}:${aggregation.toLowerCase()}`]
      : [];
  });

  return measureLabels.length > 0 ? measureLabels.sort().join('|') : null;
}

function measureSignaturesMatch(
  sources: CompositeQueryDefinition['sources'],
): boolean {
  const measureSignatures = sources.flatMap((source) => {
    const signature = getSourceMeasureSignature(source);
    return signature ? [signature] : [];
  });

  return (
    measureSignatures.length === sources.length &&
    allLabelsMatch(measureSignatures)
  );
}

function hasSameMetricSameSchemaShape(
  definition: CompositeQueryDefinition,
): boolean {
  return (
    definition.sources.length >= 2 &&
    sourceIdentityMatches(definition.sources) &&
    measureSignaturesMatch(definition.sources)
  );
}

function sourceLabelsSuggestPeriodComparison(
  definition: CompositeQueryDefinition,
): boolean {
  return textSuggestsPeriodComparison(
    definition.sources
      .map((source) => `${source.id} ${source.label ?? ''}`)
      .join(' '),
  );
}

function fieldAliasesSuggestPeriodComparison(
  definition: CompositeQueryDefinition,
): boolean {
  return textSuggestsPeriodComparison(
    definition.fields.map((field) => field.alias).join(' '),
  );
}

function computedFieldsSuggestPeriodComparison(
  definition: CompositeQueryDefinition,
): boolean {
  return textSuggestsPeriodComparison(
    (definition.computedFields ?? [])
      .map((field) => `${field.name} ${field.expression}`)
      .join(' '),
  );
}

function timeRangesSuggestPeriodComparison(
  definition: CompositeQueryDefinition,
): boolean {
  return textSuggestsPeriodComparison(
    definition.sources
      .flatMap((source) => {
        const label = getTimeRangeLabel(source.query.timeRange);
        return label ? [label] : [];
      })
      .join(' '),
  );
}

function definitionHasPeriodComparisonShape(
  definition: CompositeQueryDefinition,
): boolean {
  if (!hasSameMetricSameSchemaShape(definition)) {
    return false;
  }

  return (
    sourceLabelsSuggestPeriodComparison(definition) ||
    timeRangesSuggestPeriodComparison(definition)
  );
}

function definitionSuggestsPeriodComparison(
  definition: CompositeQueryDefinition,
): boolean {
  if (definitionHasPeriodComparisonShape(definition)) {
    return true;
  }

  if (
    !sourceLabelsSuggestPeriodComparison(definition) &&
    !timeRangesSuggestPeriodComparison(definition)
  ) {
    return false;
  }

  return (
    fieldAliasesSuggestPeriodComparison(definition) ||
    computedFieldsSuggestPeriodComparison(definition)
  );
}

function resultMetadataSuggestsPeriodComparison(
  metadata:
    | Pick<
        CreateResultMetadata,
        'resultTypeLabel' | 'intentCategory' | 'requestedIntentCategory'
      >
    | undefined,
): boolean {
  return (
    metadata?.resultTypeLabel === 'Period Comparison' ||
    metadata?.intentCategory === 'period_comparison' ||
    metadata?.requestedIntentCategory === 'period_comparison'
  );
}

function resultMetadataSuggestsDerivedMetric(
  metadata:
    | Pick<
        CreateResultMetadata,
        'intentCategory' | 'requestedIntentCategory'
      >
    | undefined,
): boolean {
  return (
    metadata?.intentCategory === 'derived_metric' ||
    metadata?.requestedIntentCategory === 'derived_metric'
  );
}

function getCompositeResultTypeLabel(
  definition: CompositeQueryDefinition,
  metadata:
    | Pick<
        CreateResultMetadata,
        'resultTypeLabel' | 'intentCategory' | 'requestedIntentCategory'
      >
    | undefined,
): 'Combined Sources' | 'Period Comparison' {
  if (resultMetadataSuggestsPeriodComparison(metadata)) {
    return 'Period Comparison';
  }

  if (resultMetadataSuggestsDerivedMetric(metadata)) {
    return 'Combined Sources';
  }

  return definitionSuggestsPeriodComparison(definition)
    ? 'Period Comparison'
    : 'Combined Sources';
}

function getCompositeComparisonTagLabels(
  definition: CompositeQueryDefinition,
  isPeriodComparison: boolean,
  prompt?: string,
): string[] {
  if (!isPeriodComparison) {
    return [];
  }

  const comparisonType = getPeriodComparisonLabel(definition, prompt);

  return comparisonType === 'period comparison' ? [] : [comparisonType];
}

function stripPeriodQualifier(label: string): string | null {
  const stripped = label
    .replace(/\b(today|yesterday|current|previous|prior|last|this)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  return stripped ? stripped : null;
}

function getPeriodComparisonSourceLabels(
  definition: CompositeQueryDefinition,
): string[] {
  const strippedSourceLabels = uniqueReadableLabels(
    definition.sources.flatMap((source) => {
      const sourceLabel = getCompositeSourceLabel(source);
      return stripPeriodQualifier(sourceLabel);
    }),
  );
  if (strippedSourceLabels.length > 0) {
    return strippedSourceLabels;
  }

  return uniqueReadableLabels(
    definition.sources.map((source) => source.query.schemaName),
  );
}

function getPeriodComparisonLabel(
  definition: CompositeQueryDefinition,
  prompt?: string,
): string {
  const text = [
    prompt,
    ...definition.sources.flatMap((source) => [
      source.id,
      source.label,
      formatTimeRangeLabel(source.query.timeRange),
    ]),
  ]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(' ')
    .toLowerCase();

  if (/\btoday\b/.test(text) && /\byesterday\b/.test(text)) {
    return 'today vs yesterday';
  }

  const timeRangeLabels = getCompositeTimeRangeLabels(definition).filter(
    (label) => label !== 'custom range',
  );
  if (timeRangeLabels.length >= 2) {
    return `${timeRangeLabels[0]} vs ${timeRangeLabels[1]}`;
  }

  return 'period comparison';
}

function getCompositeJoinConceptTags(
  definition: CompositeQueryDefinition,
  isPeriodComparison: boolean,
): string[] {
  const joinKey = definition.joinKeys[0];
  if (!joinKey) {
    return [];
  }

  const kind = normalizeLabel(joinKey.kind) ?? 'exact';
  if (kind === 'time bucket') {
    if (isPeriodComparison) {
      return ['time bucket'];
    }

    const keyText = `${joinKey.left} ${joinKey.right}`.toLowerCase();
    return /\b(date|time|datetime|timestamp)\b/.test(keyText)
      ? ['nearest timestamp']
      : ['time bucket'];
  }

  return [`${kind} match`];
}

function getPeriodComparisonFieldLabels(
  definition: CompositeQueryDefinition,
): string[] {
  return uniqueLabels([
    ...getCompositeGranularityLabels(definition),
    ...getCompositeMetricFieldLabels(definition),
    ...getCompositeComputedFieldLabels(definition),
  ]);
}

function ensurePeriodComparisonName(name: string): string {
  if (textSuggestsPeriodComparison(name)) {
    return name;
  }

  return trimLength(`${name} Comparison`, 80);
}

function buildCompositeFallbackName(
  definition: CompositeQueryDefinition,
  resultTypeLabel: 'Combined Sources' | 'Period Comparison',
): string {
  const sourceLabels = getCompositeSourceLabels(definition);
  const sourceName =
    sourceLabels.length > 0
      ? sourceLabels.join(resultTypeLabel === 'Period Comparison' ? ' vs ' : ' and ')
      : resultTypeLabel;
  const name =
    resultTypeLabel === 'Period Comparison'
      ? `${sourceName} Comparison`
      : `${sourceName} Combined`;

  return trimLength(toTitleCase(name), 80);
}

function buildCompositeDescription(
  definition: CompositeQueryDefinition,
  resultTypeLabel: 'Combined Sources' | 'Period Comparison',
  prompt?: string,
): string {
  if (resultTypeLabel === 'Period Comparison') {
    const sourceSummary = formatReadableList(getPeriodComparisonSourceLabels(definition));
    const comparisonLabel = getPeriodComparisonLabel(definition, prompt);
    const granularityLabels = getCompositeGranularityLabels(definition);
    const granularitySummary = granularityLabels[0]
      ? ` by ${granularityLabels[0]}`
      : '';
    const fieldSummary = getPeriodComparisonFieldLabels(definition).join(', ');
    const computedFieldSentence = getCompositeComputedFieldSentence(definition);

    return [
      `Compares ${sourceSummary || 'generated sources'} ${comparisonLabel}${granularitySummary}.`,
      `Fields: ${fieldSummary || 'generated fields'}.`,
      computedFieldSentence,
      `Join: ${getPeriodComparisonJoinSummary(definition)}.`,
      `Result: ${resultTypeLabel}.`,
    ]
      .filter((part): part is string => part !== null)
      .join(' ');
  }

  const sourceSummary = formatReadableList(getCompositeSourceLabels(definition));
  const computedFieldSentence = getCompositeComputedFieldSentence(definition);
  const timeRangeSummary = getCompositeConciseTimeRangeDescription(definition);

  return [
    `Combines ${sourceSummary || 'generated sources'} over ${timeRangeSummary}.`,
    `Fields: ${getCompositeFieldDescription(definition)}.`,
    computedFieldSentence,
    `Join: ${getCompositeJoinSummary(definition)}.`,
    `Result: ${resultTypeLabel}.`,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');
}

function getCompositeTags(
  definition: CompositeQueryDefinition,
  resultTypeLabel: 'Combined Sources' | 'Period Comparison',
  prompt?: string,
): string[] {
  const isPeriodComparison = resultTypeLabel === 'Period Comparison';

  if (isPeriodComparison) {
    return uniqueLabels([
      ...getPeriodComparisonSourceLabels(definition),
      ...getCompositeComparisonTagLabels(definition, isPeriodComparison, prompt),
      ...getPeriodComparisonFieldLabels(definition),
      ...getCompositeJoinConceptTags(definition, true),
      resultTypeLabel,
    ]);
  }

  const metricFieldLabels = getCompositeMetricFieldLabels(definition);

  return uniqueLabels([
    ...metricFieldLabels,
    ...getCompositeSourceLabels(definition),
    ...getCompositeComputedFieldLabels(definition),
    ...getCompositeTimeRangeLabels(definition),
    ...getCompositeJoinConceptTags(definition, false),
    resultTypeLabel,
  ]);
}

function buildSingleSourceDescription(
  sourceName: string,
  query: TemplateQuery | undefined,
  resultTypeLabel: string,
  defaultTimeRange?: unknown,
  defaultGranularity?: unknown,
): string {
  const timeRangeSummary =
    getTimeRangeLabel(query?.timeRange, defaultTimeRange) ?? 'all available time';
  if (shouldUseUserFacingFieldMetadata(query)) {
    const fieldSummary = getUserFacingMeasureFieldLabels(query).join(', ');
    return `Source: ${sourceName}. Fields: ${fieldSummary}. Time range: ${timeRangeSummary}. Result: ${resultTypeLabel}.`;
  }

  const metricSummary =
    query?.measures.map(getMeasureSummary).join(', ') || 'generated metric';
  const aggregationSummary =
    uniqueLabels(
      query?.measures.map((measure) => measure.aggregation) ?? [],
    ).join(', ') || 'generated aggregation';
  const groupBySummary =
    getGroupByDescription(query?.groupBy, defaultGranularity) ?? 'no grouping';

  return `Source: ${sourceName}. Metric: ${metricSummary}. Aggregation: ${aggregationSummary}. Time range: ${timeRangeSummary}. Grouping: ${groupBySummary}. Result: ${resultTypeLabel}.`;
}

function getSingleSourceTags(
  sourceName: string,
  query: TemplateQuery | undefined,
  resultTypeLabel: string,
  defaultTimeRange?: unknown,
  defaultGranularity?: unknown,
): string[] {
  if (shouldUseUserFacingFieldMetadata(query)) {
    return uniqueLabels([
      sourceName,
      ...getUserFacingMeasureFieldLabels(query).map((label) =>
        label.toLowerCase(),
      ),
      resultTypeLabel,
    ]);
  }

  const measureTags =
    query?.measures.flatMap((measure) => {
      if (isGenericCountMeasure(measure)) {
        const aliasLabel = getUserFacingMeasureAliasLabel(measure);
        const aliasTag =
          aliasLabel && aliasLabel.toLowerCase() !== 'count'
            ? measure.alias
            : null;
        return [aliasTag, measure.aggregation];
      }

      const fieldTag =
        measure.alias && isGenericMeasureFieldLabel(measure.field)
          ? null
          : getMeasureFieldLabel(measure);
      return [measure.alias, fieldTag, measure.aggregation];
    }) ?? [];
  const timeRangeLabel = getTimeRangeLabel(query?.timeRange, defaultTimeRange);
  const groupByLabels = getGroupByLabels(query?.groupBy, defaultGranularity);

  return uniqueLabels([
    sourceName,
    ...measureTags,
    timeRangeLabel,
    ...groupByLabels,
    resultTypeLabel,
  ]);
}

function buildGeneratedFromDescription(prompt: string | undefined): string {
  if (!prompt || hasUnresolvedPlaceholder(prompt)) {
    return '';
  }

  return `Generated from: "${prompt.trim()}"`;
}

function cleanUnresolvedPlaceholderText(value: string): string {
  return value
    .replace(UNRESOLVED_PLACEHOLDER_PATTERN, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+\./g, '.')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanMetadataTagString(tags: string): string {
  return buildTagString(
    tags
      .split(',')
      .map(cleanUnresolvedPlaceholderText)
      .filter(isConciseMetadataTag),
    true,
  );
}

function sanitizeTemplateSaveMetadata(
  metadata: TemplateSaveMetadata,
): TemplateSaveMetadata {
  return {
    name: cleanUnresolvedPlaceholderText(metadata.name),
    description: cleanUnresolvedPlaceholderText(metadata.description),
    tags: cleanMetadataTagString(metadata.tags),
    folder: cleanUnresolvedPlaceholderText(metadata.folder),
  };
}

export function buildTemplateSaveMetadataFromCompositeDefinition(
  definition: CompositeQueryDefinition,
  prompt?: string,
  _explanation?: string,
  resultMetadata?: Pick<
    CreateResultMetadata,
    'resultTypeLabel' | 'intentCategory' | 'requestedIntentCategory'
  >,
): TemplateSaveMetadata {
  const resultTypeLabel = getCompositeResultTypeLabel(
    definition,
    resultMetadata,
  );
  const promptName = promptToTemplateName(prompt);
  const name =
    resultTypeLabel === 'Period Comparison' && promptName
      ? ensurePeriodComparisonName(promptName)
      : promptName ?? buildCompositeFallbackName(definition, resultTypeLabel);

  return sanitizeTemplateSaveMetadata({
    name,
    description: buildCompositeDescription(definition, resultTypeLabel, prompt),
    tags: buildCuratedTagString(
      getCompositeTags(definition, resultTypeLabel, prompt),
      8,
    ),
    folder:
      resultTypeLabel === 'Period Comparison'
        ? 'Period Comparisons'
        : 'Combined Sources',
  });
}

export function buildTemplateSaveMetadataFromCreateResult(
  result: CreateTemplateResult,
  prompt?: string,
): TemplateSaveMetadata {
  if (result.kind === 'composite') {
    return buildTemplateSaveMetadataFromCompositeDefinition(
      result.definition,
      prompt,
      result.explanation,
      result.metadata,
    );
  }

  if (result.kind === 'template') {
    const query = result.response.templateQuery;
    const defaultTimeRange = result.response.templateVariables?.timeRange?.default;
    const defaultGranularity =
      result.response.templateVariables?.granularity?.default;
    const sourceName =
      normalizeLabel(result.response.source?.name) ??
      normalizeLabel(query?.schemaId) ??
      'Template';
    const promptName = promptToTemplateName(prompt);

    return sanitizeTemplateSaveMetadata({
      name:
        promptName ??
        buildSingleSourceFallbackName(sourceName, query, defaultGranularity),
      description: buildSingleSourceDescription(
        sourceName,
        query,
        result.metadata.resultTypeLabel,
        defaultTimeRange,
        defaultGranularity,
      ),
      tags: buildCuratedTagString(
        getSingleSourceTags(
          sourceName,
          query,
          result.metadata.resultTypeLabel,
          defaultTimeRange,
          defaultGranularity,
        ),
        8,
      ),
      folder: sourceName,
    });
  }

  return sanitizeTemplateSaveMetadata({
    ...EMPTY_TEMPLATE_SAVE_METADATA,
    name: promptToTemplateName(prompt) ?? '',
    description: buildGeneratedFromDescription(prompt),
  });
}
